import Decimal from 'decimal.js';

/**
 * P10 – Mua hàng (docs/phases/P10.md). B2: đề nghị mua → hỏi giá nhiều NCC → so sánh → chọn NCC.
 * Phiếu nhập kho thật làm trên Asia (DEC-001 A). Giá NCC: CHƯA VAT, 1 ĐVT chính của mã.
 */
export const PURCHASE_REQUEST_CODE_SUFFIX = 'DNM';

export const PURCHASE_REQUEST_STATUSES = ['draft', 'quoting', 'awarded', 'ordered', 'cancelled'] as const;
export type PurchaseRequestStatus = (typeof PURCHASE_REQUEST_STATUSES)[number];

export const PURCHASE_REQUEST_STATUS_LABELS: Record<PurchaseRequestStatus, string> = {
  draft: 'Mới lập',
  quoting: 'Đang hỏi giá',
  awarded: 'Đã chọn NCC',
  ordered: 'Đã lên đơn mua',
  cancelled: 'Đã hủy',
};

/** Còn sửa dòng hàng / hỏi giá / chọn NCC được. */
export const PURCHASE_REQUEST_OPEN_STATUSES: readonly PurchaseRequestStatus[] = ['draft', 'quoting', 'awarded'];

export const PURCHASE_RFQ_STATUSES = ['draft', 'sent', 'replied', 'declined', 'cancelled'] as const;
export type PurchaseRfqStatus = (typeof PURCHASE_RFQ_STATUSES)[number];

export const PURCHASE_RFQ_STATUS_LABELS: Record<PurchaseRfqStatus, string> = {
  draft: 'Chưa gửi',
  sent: 'Đã gửi, chờ báo giá',
  replied: 'NCC đã báo giá',
  declined: 'NCC từ chối',
  cancelled: 'Đã hủy',
};

// ───────────────────────── Bảng so sánh ─────────────────────────

export interface CompareItem {
  id: number;
  quantity: number;
}
export interface CompareOffer {
  rfqId: number;
  rfqLineId: number;
  requestItemId: number;
  supplierId: number;
  unitPrice: number | null;
  leadDays: number | null;
}
export interface CompareResult {
  /** requestItemId → dòng hỏi giá rẻ nhất (cùng giá: giao nhanh hơn, rồi rfqLineId nhỏ hơn) */
  cheapest: Map<number, number>;
  /** supplierId → tổng tiền chưa VAT các dòng NCC có báo, số dòng có báo */
  bySupplier: Map<number, { amount: number; quoted: number }>;
  /** NCC báo đủ mọi dòng có tổng thấp nhất (mua trọn 1 NCC), null nếu không NCC nào báo đủ */
  bestFullSupplierId: number | null;
  /** Tổng tiền nếu mỗi dòng chọn NCC rẻ nhất (dòng chưa ai báo không tính) */
  cheapestTotal: number;
}

/** So sánh báo giá NCC: rẻ nhất từng dòng + tổng theo NCC + NCC rẻ nhất báo đủ. Tiền làm tròn đồng (VND). */
export function compareSupplierOffers(items: CompareItem[], offers: CompareOffer[]): CompareResult {
  const qty = new Map(items.map((i) => [i.id, i.quantity]));
  const best = new Map<number, CompareOffer>();
  const bySupplier = new Map<number, { amount: number; quoted: number }>();
  for (const o of offers) {
    if (o.unitPrice === null || !qty.has(o.requestItemId)) continue;
    const cur = best.get(o.requestItemId);
    const better =
      !cur ||
      o.unitPrice < cur.unitPrice! ||
      (o.unitPrice === cur.unitPrice && (o.leadDays ?? Infinity) < (cur.leadDays ?? Infinity)) ||
      (o.unitPrice === cur.unitPrice && (o.leadDays ?? Infinity) === (cur.leadDays ?? Infinity) && o.rfqLineId < cur.rfqLineId);
    if (better) best.set(o.requestItemId, o);
    const s = bySupplier.get(o.supplierId) ?? { amount: 0, quoted: 0 };
    s.amount += Math.round(o.unitPrice * qty.get(o.requestItemId)!);
    s.quoted += 1;
    bySupplier.set(o.supplierId, s);
  }
  let bestFullSupplierId: number | null = null;
  let bestFullAmount = Infinity;
  for (const [sid, s] of bySupplier) {
    if (s.quoted === items.length && items.length > 0 && s.amount < bestFullAmount) {
      bestFullAmount = s.amount;
      bestFullSupplierId = sid;
    }
  }
  let cheapestTotal = 0;
  for (const [itemId, o] of best) cheapestTotal += Math.round(o.unitPrice! * qty.get(itemId)!);
  return { cheapest: new Map([...best].map(([k, v]) => [k, v.rfqLineId])), bySupplier, bestFullSupplierId, cheapestTotal };
}

// ───────────────────────── B3: Đơn mua (PO), hàng về ─────────────────────────

export const PURCHASE_ORDER_CODE_SUFFIX = 'DMH';

export const PURCHASE_ORDER_STATUSES = ['draft', 'confirmed', 'partially_received', 'received', 'closed', 'cancelled'] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: 'Mới lập',
  confirmed: 'Đã đặt hàng',
  partially_received: 'Về một phần',
  received: 'Đã về đủ',
  closed: 'Đóng (giao thiếu)',
  cancelled: 'Đã hủy',
};

/** Đơn còn chờ hàng về (theo dõi trễ hẹn). */
export const PURCHASE_ORDER_WAITING_STATUSES: readonly PurchaseOrderStatus[] = ['confirmed', 'partially_received'];

/**
 * DEC-156 (mặc định): đơn mua tổng (gồm VAT) từ mức này trở lên cần quyền purchase_order.approve (Giám đốc) mới đặt hàng được;
 * dưới mức thì Mua hàng tự đặt.
 */
export const PURCHASE_ORDER_APPROVAL_LIMIT = 200_000_000;

/** Dung sai SL hàng về (hàng cân kg, DEC-156 mặc định ±3%): về ≥ 97% SL đặt coi như đủ dòng; về quá 103% thì chặn. */
export const RECEIPT_TOLERANCE_PERCENT = 3;

export function lineFullyReceived(ordered: number, received: number): boolean {
  return received >= ordered * (1 - RECEIPT_TOLERANCE_PERCENT / 100) - 1e-9;
}

export function maxReceivable(ordered: number, received: number): number {
  return Math.max(0, Math.round((ordered * (1 + RECEIPT_TOLERANCE_PERCENT / 100) - received) * 1000) / 1000);
}

// ───────────────────────── B4: Điểm hiệu suất NCC (DEC-157 mặc định) ─────────────────────────

/** Tính điểm trên đơn mua / phiếu hỏi giá trong ngần này ngày gần nhất. */
export const SUPPLIER_SCORE_WINDOW_DAYS = 365;

/** Trọng số điểm tổng: đúng hẹn 40, giao đủ 30, giá cạnh tranh 30. Thiếu số liệu phần nào thì chia lại theo phần còn lại. */
export const SUPPLIER_SCORE_WEIGHTS = { onTime: 40, fill: 30, price: 30 } as const;

export interface SupplierPerformanceInput {
  /** Đơn đã về đủ / đóng đúng hẹn. */
  ordersOnTime: number;
  /** Đơn về trễ, đóng thiếu không có hàng, hoặc đang chờ mà quá hẹn. */
  ordersLate: number;
  /** Tỷ lệ giao đủ 0–1 (trung bình từng dòng, tối đa 1) trên đơn đã về đủ / đóng; null = chưa có. */
  fillRate: number | null;
  /** Giá rẻ nhất / giá NCC này, trung bình trên các dòng có ≥ 2 NCC báo giá (0–1, 1 = luôn rẻ nhất); null = chưa có. */
  priceIndex: number | null;
}

export type SupplierGrade = 'A' | 'B' | 'C' | 'D';

export interface SupplierScore {
  onTimeRate: number | null;
  score: number | null;
  grade: SupplierGrade | null;
}

export function supplierScore(i: SupplierPerformanceInput): SupplierScore {
  const delivered = i.ordersOnTime + i.ordersLate;
  const onTimeRate = delivered > 0 ? i.ordersOnTime / delivered : null;
  const parts: [number | null, number][] = [
    [onTimeRate, SUPPLIER_SCORE_WEIGHTS.onTime],
    [i.fillRate, SUPPLIER_SCORE_WEIGHTS.fill],
    [i.priceIndex, SUPPLIER_SCORE_WEIGHTS.price],
  ];
  let sum = 0;
  let weight = 0;
  for (const [v, w] of parts) {
    if (v === null) continue;
    sum += Math.min(1, Math.max(0, v)) * w;
    weight += w;
  }
  if (!weight) return { onTimeRate, score: null, grade: null };
  const score = Math.round((sum / weight) * 100);
  return { onTimeRate, score, grade: score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 50 ? 'C' : 'D' };
}

/**
 * DEC-298 – Chi phí mua của đơn (vận chuyển, bốc xếp, tờ khai, thuế NK, xe nhà đi lấy hàng), CHƯA VAT.
 * Anh Tiến 12/10/2026: "cộng hết các chi phí của đơn hàng đó, kể cả chi phí xe cty để hình thành giá mua trước VAT".
 */
export const PURCHASE_COST_KINDS = ['shipping', 'loading', 'customs', 'import_tax', 'own_truck', 'other'] as const;
export type PurchaseCostKind = (typeof PURCHASE_COST_KINDS)[number];

export const PURCHASE_COST_KIND_LABELS: Record<PurchaseCostKind, string> = {
  shipping: 'Vận chuyển thuê ngoài',
  loading: 'Bốc xếp',
  customs: 'Tờ khai / hải quan',
  import_tax: 'Thuế nhập khẩu',
  own_truck: 'Xe công ty đi lấy hàng',
  other: 'Chi phí khác',
};

/** Xe nhà: tiền = km × đ/km, làm tròn đồng. */
export function ownTruckCost(km: number, ratePerKm: number): number {
  return new Decimal(km).mul(ratePerKm).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * Phân bổ tổng chi phí mua vào từng dòng theo tỉ lệ giá trị hàng chưa VAT (net_amount).
 * Trả về chi phí / 1 ĐVT chính của từng dòng (2 số lẻ, ROUND_HALF_UP). Đơn chưa có giá trị hàng → chia theo SL.
 */
export function allocateLandedCost(
  lines: { id: number; quantity: number; netAmount: number }[],
  totalCost: number,
): Map<number, number> {
  const out = new Map<number, number>();
  const valid = lines.filter((l) => Number(l.quantity) > 0);
  for (const l of lines) out.set(l.id, 0);
  if (!valid.length || !(totalCost > 0)) return out;
  const sumNet = valid.reduce((s, l) => s.plus(Math.max(0, Number(l.netAmount))), new Decimal(0));
  const sumQty = valid.reduce((s, l) => s.plus(Number(l.quantity)), new Decimal(0));
  for (const l of valid) {
    const share = sumNet.gt(0) ? new Decimal(Math.max(0, Number(l.netAmount))).div(sumNet) : new Decimal(l.quantity).div(sumQty);
    const perUnit = share.mul(totalCost).div(l.quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    out.set(l.id, perUnit.toNumber());
  }
  return out;
}
